import React, { useState } from "react"
import {
    Box,
    Modal,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined"
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined"

export default function AttendanceSheetModal({ open, onClose, attendanceSheet }) {
    const [viewMode, setViewMode] = useState("table") // "table" | "list"

    if (!attendanceSheet) return null

    const formatDate = (dateStr) => {
        if (!dateStr) return ""
        const date = new Date(dateStr)
        const day = String(date.getDate()).padStart(2, "0")
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const year = date.getFullYear()
        return `${day}/${month}/${year}`
    }

    const formatWeekRange = (startDate, endDate) => {
        if (!startDate || !endDate) return ""
        return `${formatDate(startDate)} - ${formatDate(endDate)}`
    }

    const getAttendanceTypeDisplay = (attendanceType) => {
        const typeUpper = attendanceType?.toUpperCase() || ""
        if (typeUpper === "FULL_DAY" || typeUpper === "FULLDAY") return "full"
        if (typeUpper === "HALF_DAY" || typeUpper === "HALFDAY") return "half"
        if (typeUpper === "ABSENT") return "absent"
        return attendanceType || "-"
    }

    const getAttendanceTypeColor = (attendanceType) => {
        const typeUpper = attendanceType?.toUpperCase() || ""
        if (typeUpper === "FULL_DAY" || typeUpper === "FULLDAY") return { bg: "#F2F4F7", color: "#344054" }
        if (typeUpper === "HALF_DAY" || typeUpper === "HALFDAY") return { bg: "#FEF0C7", color: "#B54708" }
        if (typeUpper === "ABSENT") return { bg: "#FEE4E2", color: "#D92D20" }
        return { bg: "#F2F4F7", color: "#344054" }
    }

    // Check if attendance is present
    const isPresent = (attendanceType) => {
        if (!attendanceType) return false
        const typeUpper = attendanceType.toUpperCase()
        return typeUpper === "FULL_DAY" || typeUpper === "FULLDAY" || typeUpper === "HALF_DAY" || typeUpper === "HALFDAY"
    }

    // Generate week days from start and end dates
    const generateWeekDays = () => {
        if (!attendanceSheet.weekStartDate || !attendanceSheet.weekEndDate) return []
        
        const start = new Date(attendanceSheet.weekStartDate)
        const days = []
        const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
        
        for (let i = 0; i < 7; i++) {
            const currentDate = new Date(start)
            currentDate.setDate(start.getDate() + i)
            days.push({
                day: dayNames[currentDate.getDay()],
                date: `${String(currentDate.getDate()).padStart(2, "0")}/${String(currentDate.getMonth() + 1).padStart(2, "0")}`,
                fullDate: currentDate.toISOString().split('T')[0],
            })
        }
        return days
    }

    const weekDays = generateWeekDays()

    // Build attendance map for grid view
    const attendanceMap = {}
    attendanceSheet.attendanceRecords?.forEach((record) => {
        if (!attendanceMap[record.id]) {
            attendanceMap[record.id] = {}
        }
        record.schedule?.forEach((scheduleItem) => {
            attendanceMap[record.id][scheduleItem.date] = scheduleItem.attendanceType
        })
    })

    // Flatten attendance records into table rows (one row per worker-date combination)
    const allTableRows = []
    attendanceSheet.attendanceRecords?.forEach((record) => {
        record.schedule?.forEach((scheduleItem) => {
            allTableRows.push({
                workerId: record.id,
                firstName: record.firstName,
                lastName: record.lastName,
                speciality: record.speciality,
                date: scheduleItem.date,
                attendanceType: scheduleItem.attendanceType,
                remarks: scheduleItem.remarks,
            })
        })
    })

    // For list view, only show rows with attendance (not absences)
    const tableRows = viewMode === "list"
        ? allTableRows.filter(row => isPresent(row.attendanceType))
        : allTableRows

    // Sort by date, then by worker name
    tableRows.sort((a, b) => {
        const dateCompare = new Date(a.date) - new Date(b.date)
        if (dateCompare !== 0) return dateCompare
        return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
    })

    // Calculate totals
    const workers = attendanceSheet.attendanceRecords || []
    const totalWorkers = new Set(attendanceSheet.attendanceRecords?.map((r) => r.id)).size
    const totalHours = allTableRows.reduce((sum, row) => {
        const type = row.attendanceType?.toUpperCase()
        if (type === "FULL_DAY" || type === "FULLDAY") return sum + 8
        if (type === "HALF_DAY" || type === "HALFDAY") return sum + 4
        return sum
    }, 0)

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Box
                sx={{
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    border: "1px solid #E4E7EC",
                    width: "90%",
                    maxWidth: "1200px",
                    maxHeight: "90vh",
                    overflow: "auto",
                    boxShadow: "0px 8px 24px rgba(0,0,0,0.1)",
                    position: "relative",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 3,
                        borderBottom: "1px solid #E4E7EC",
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#fff",
                        zIndex: 1,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                backgroundColor: "#F2F4F7",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography sx={{ fontSize: "20px", fontWeight: 600, color: "#667085" }}>
                                {attendanceSheet.projectName?.[0]?.toUpperCase() || "P"}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "20px",
                                    color: "#101828",
                                }}
                            >
                                {attendanceSheet.projectName || "Weekly Schedule"}
                            </Typography>
                            <Typography sx={{ color: "#667085", fontSize: "14px", mt: 0.5 }}>
                                {formatWeekRange(attendanceSheet.weekStartDate, attendanceSheet.weekEndDate)}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: "#4D6BF6",
                            "&:hover": { backgroundColor: "#F4F3FF" },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* View Toggle */}
                <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #E4E7EC" }}>
                    <Box
                        sx={{
                            display: "inline-flex",
                            backgroundColor: "#F2F4F7",
                            borderRadius: "6px",
                            padding: "3px",
                            gap: "3px",
                        }}
                    >
                        <Box
                            onClick={() => setViewMode("table")}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                px: 1.5,
                                py: 0.5,
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontWeight: 500,
                                transition: "all 0.15s ease",
                                backgroundColor: viewMode === "table" ? "#fff" : "transparent",
                                color: viewMode === "table" ? "#101828" : "#667085",
                                boxShadow: viewMode === "table" ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                                "&:hover": {
                                    backgroundColor: viewMode === "table" ? "#fff" : "#E4E7EC",
                                },
                            }}
                        >
                            <GridViewOutlinedIcon sx={{ fontSize: 14 }} />
                            Weekly Grid
                        </Box>
                        <Box
                            onClick={() => setViewMode("list")}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                px: 1.5,
                                py: 0.5,
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontWeight: 500,
                                transition: "all 0.15s ease",
                                backgroundColor: viewMode === "list" ? "#fff" : "transparent",
                                color: viewMode === "list" ? "#101828" : "#667085",
                                boxShadow: viewMode === "list" ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                                "&:hover": {
                                    backgroundColor: viewMode === "list" ? "#fff" : "#E4E7EC",
                                },
                            }}
                        >
                            <ViewListOutlinedIcon sx={{ fontSize: 14 }} />
                            Attendance Only
                        </Box>
                    </Box>
                </Box>

                {/* Table Content */}
                <TableContainer sx={{ maxHeight: "60vh" }}>
                    {viewMode === "table" ? (
                        /* Weekly Grid View */
                        <Table stickyHeader sx={{ tableLayout: "fixed" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            color: "#101828",
                                            fontSize: "13px",
                                            backgroundColor: "#F9FAFB",
                                            width: "180px",
                                            padding: "8px 12px",
                                        }}
                                    >
                                        Worker
                                    </TableCell>
                                    {weekDays.map((day) => (
                                        <TableCell
                                            key={day.fullDate}
                                            align="center"
                                            sx={{
                                                fontWeight: 600,
                                                color: "#344054",
                                                fontSize: "12px",
                                                backgroundColor: "#F9FAFB",
                                                padding: "8px 4px",
                                                borderLeft: "1px solid #E4E7EC",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                <Typography sx={{ fontWeight: 600, fontSize: "11px" }}>{day.day}</Typography>
                                                <Typography sx={{ fontSize: "10px", color: "#667085" }}>{day.date}</Typography>
                                            </Box>
                                        </TableCell>
                                    ))}
                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#101828",
                                            fontSize: "11px",
                                            backgroundColor: "#F9FAFB",
                                            padding: "8px 4px",
                                            width: "60px",
                                        }}
                                    >
                                        Hours
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {workers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={weekDays.length + 2} align="center" sx={{ py: 4 }}>
                                            <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                                                No workers found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    workers.map((worker) => {
                                        const workerHours = weekDays.reduce((sum, day) => {
                                            const type = attendanceMap[worker.id]?.[day.fullDate]?.toUpperCase()
                                            if (type === "FULL_DAY" || type === "FULLDAY") return sum + 8
                                            if (type === "HALF_DAY" || type === "HALFDAY") return sum + 4
                                            return sum
                                        }, 0)

                                        return (
                                            <TableRow key={worker.id}>
                                                <TableCell sx={{ padding: "6px 12px" }}>
                                                    <Typography sx={{ fontWeight: 600, color: "#101828", fontSize: "13px" }}>
                                                        {worker.firstName} {worker.lastName}
                                                    </Typography>
                                                    <Typography sx={{ color: "#667085", fontSize: "11px" }}>
                                                        {worker.speciality}
                                                    </Typography>
                                                </TableCell>
                                                {weekDays.map((day) => {
                                                    const attendanceType = attendanceMap[worker.id]?.[day.fullDate]
                                                    const present = isPresent(attendanceType)
                                                    return (
                                                        <TableCell
                                                            key={day.fullDate}
                                                            align="center"
                                                            sx={{
                                                                padding: "4px",
                                                                borderLeft: "1px solid #E4E7EC",
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "14px",
                                                                    fontWeight: 600,
                                                                    color: present ? "#101828" : "#D0D5DD",
                                                                }}
                                                            >
                                                                {present ? "+" : "−"}
                                                            </Typography>
                                                        </TableCell>
                                                    )
                                                })}
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        padding: "4px",
                                                        fontWeight: 600,
                                                        fontSize: "12px",
                                                        color: "#101828",
                                                    }}
                                                >
                                                    {workerHours}h
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                )}
                            </TableBody>
                        </Table>
                    ) : (
                        /* List View - Attendance Only */
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        color: "#101828",
                                        fontSize: "14px",
                                        backgroundColor: "#F9FAFB",
                                    }}
                                >
                                    Worker
                                </TableCell>
                                <TableCell
                                    align="left"
                                    sx={{
                                        fontWeight: 600,
                                        color: "#101828",
                                        fontSize: "14px",
                                        backgroundColor: "#F9FAFB",
                                    }}
                                >
                                    Date
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 600,
                                        color: "#101828",
                                        fontSize: "14px",
                                        backgroundColor: "#F9FAFB",
                                    }}
                                >
                                    Duration
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableRows.length === 0 ? (
                                <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                        <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                                            No attendance records found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tableRows.map((row, index) => {
                                    const typeColor = getAttendanceTypeColor(row.attendanceType)
                                    return (
                                        <TableRow key={`${row.workerId}-${row.date}-${index}`}>
                                            <TableCell>
                                                <Typography sx={{ fontWeight: 600, color: "#101828", fontSize: "14px" }}>
                                                    {row.firstName} {row.lastName}
                                                </Typography>
                                                <Typography sx={{ color: "#667085", fontSize: "12px", mt: 0.5 }}>
                                                    {row.speciality}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ color: "#101828", fontSize: "14px" }}>
                                                    {formatDate(row.date)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={getAttendanceTypeDisplay(row.attendanceType)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: typeColor.bg,
                                                        color: typeColor.color,
                                                        fontWeight: 500,
                                                        fontSize: "12px",
                                                        height: "24px",
                                                        textTransform: "capitalize",
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            )}
                        </TableBody>
                    </Table>
                    )}
                </TableContainer>

                {/* Footer Summary */}
                <Box
                    sx={{
                        p: 3,
                        borderTop: "1px solid #E4E7EC",
                        backgroundColor: "#F9FAFB",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                        <Typography sx={{ color: "#344054", fontSize: "14px" }}>
                            Total Workers: <strong>{totalWorkers}</strong>
                        </Typography>
                        <Typography sx={{ color: "#344054", fontSize: "14px" }}>
                            Total Hours: <strong>{totalHours}h</strong>
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Modal>
    )
}

